import React, { useState, useEffect } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../../common/contexts/AuthContext';
import { Card } from 'primereact/card';
import { ProgressSpinner } from 'primereact/progressspinner';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Bar, Line, Pie, Doughnut } from 'react-chartjs-2';
import StatCard from './StatCard';
import { useCurrency } from '../../common/utils/currency';
import {
  getDashboardStats,
  getAttendanceTrends,
  getGradeDistribution,
  getGenderDistribution,
  getFeeCollection,
  getLeaveStats
} from '../../api/dashboardApi';
import '../../styles/Dashboard.scss';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const Dashboard = () => {
  const navigate = useNavigate();
  const currency = useCurrency();
  const { user, impersonatedUser } = useAuth() || {};
  // Use impersonated user's role when admin is doing level-2 impersonation
  const effectiveUser = impersonatedUser || user;
  const roleName = effectiveUser?.roleName || effectiveUser?.role?.name || effectiveUser?.Role?.name;
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [attendanceTrends, setAttendanceTrends] = useState([]);
  const [gradeDistribution, setGradeDistribution] = useState([]);
  const [genderDistribution, setGenderDistribution] = useState([]);
  const [feeCollection, setFeeCollection] = useState([]);
  const [leaveStats, setLeaveStats] = useState({ byStatus: [], byType: [] });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [statsRes, trendsRes, gradeRes, genderRes, feeRes, leaveRes] = await Promise.all([
        getDashboardStats(),
        getAttendanceTrends(14),
        getGradeDistribution(),
        getGenderDistribution(),
        getFeeCollection(),
        getLeaveStats()
      ]);

      if (statsRes.success) setStats(statsRes.data);
      if (trendsRes.success) setAttendanceTrends(trendsRes.data);
      if (gradeRes.success) setGradeDistribution(gradeRes.data);
      if (genderRes.success) setGenderDistribution(genderRes.data);
      if (feeRes.success) setFeeCollection(feeRes.data);
      if (leaveRes.success) setLeaveStats(leaveRes.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Chart color palette
  const colors = {
    primary: '#3B82F6',
    success: '#22C55E',
    warning: '#F59E0B',
    danger: '#EF4444',
    purple: '#8B5CF6',
    teal: '#14B8A6',
    pink: '#EC4899',
    indigo: '#6366F1'
  };

  // Attendance Trends Chart
  const attendanceChartData = {
    labels: attendanceTrends.map(d => new Date(d.date).toLocaleDateString('en-US', { weekday: 'short', day: 'numeric' })),
    datasets: [
      {
        label: 'Attendance Rate (%)',
        data: attendanceTrends.map(d => parseFloat(d.rate)),
        borderColor: colors.primary,
        backgroundColor: `${colors.primary}20`,
        fill: true,
        tension: 0.4
      }
    ]
  };

  const attendanceChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      title: { display: false }
    },
    scales: {
      y: {
        min: 0,
        max: 100,
        ticks: { callback: value => `${value}%` }
      }
    }
  };

  // Grade Distribution Chart
  const gradeChartData = {
    labels: gradeDistribution.map(d => d.grade),
    datasets: [{
      label: 'Students',
      data: gradeDistribution.map(d => d.count),
      backgroundColor: [
        colors.primary, colors.success, colors.warning, colors.danger,
        colors.purple, colors.teal, colors.pink, colors.indigo,
        '#64748B', '#0EA5E9', '#84CC16', '#F97316',
        '#A855F7', '#06B6D4', '#FB7185', '#818CF8'
      ],
      borderRadius: 6
    }]
  };

  const gradeChartOptions = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false }
    },
    scales: {
      x: { beginAtZero: true }
    }
  };

  // Gender Distribution Chart
  const genderChartData = {
    labels: genderDistribution.map(d => d.gender),
    datasets: [{
      data: genderDistribution.map(d => d.count),
      backgroundColor: [colors.primary, colors.pink, colors.purple],
      borderWidth: 0
    }]
  };

  const pieChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom' }
    }
  };

  // Fee Status Chart
  const feeStatusData = stats ? {
    labels: ['Paid', 'Unpaid', 'Overdue'],
    datasets: [{
      data: [
        stats.feeCollection.paid,
        stats.feeCollection.unpaid,
        stats.feeCollection.overdue
      ],
      backgroundColor: [colors.success, colors.warning, colors.danger],
      borderWidth: 0
    }]
  } : { labels: [], datasets: [] };

  // Fee Collection by Month Chart
  const feeMonthlyData = {
    labels: feeCollection.map(f => f.month),
    datasets: [
      {
        label: 'Paid',
        data: feeCollection.map(f => f.paid),
        backgroundColor: colors.success,
        borderRadius: 4
      },
      {
        label: 'Unpaid',
        data: feeCollection.map(f => f.unpaid),
        backgroundColor: colors.warning,
        borderRadius: 4
      },
      {
        label: 'Overdue',
        data: feeCollection.map(f => f.overdue),
        backgroundColor: colors.danger,
        borderRadius: 4
      }
    ]
  };

  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top' }
    },
    scales: {
      x: { stacked: false },
      y: {
        beginAtZero: true,
        ticks: { callback: value => `${currency ? currency + " " : ""}${(value/1000).toFixed(0)}k` }
      }
    }
  };

  // Leave Status Chart
  const leaveStatusData = {
    labels: leaveStats.byStatus.map(l => l.status.charAt(0).toUpperCase() + l.status.slice(1)),
    datasets: [{
      data: leaveStats.byStatus.map(l => l.count),
      backgroundColor: [colors.warning, colors.success, colors.danger, colors.purple],
      borderWidth: 0
    }]
  };

  if (roleName === "Teacher") return <Navigate to="/index/teacher/dashboard" replace />;
  if (roleName === "Parent") return <Navigate to="/index/parent/dashboard" replace />;

  if (loading) {
    return (
      <div className="dashboard-loading flex justify-content-center align-items-center" style={{ height: '80vh' }}>
        <ProgressSpinner />
      </div>
    );
  }

  return (
    <div className="dashboard p-4">
      <div className="dashboard-header mb-4">
        <h1 className="text-3xl font-bold text-800 mb-2">Dashboard</h1>
        <p className="text-500">Welcome back! Here's an overview of your school.</p>
        {stats?.activeSession && (
          <span className="session-badge mt-2">
            <i className="pi pi-calendar mr-2"></i>
            Academic Session: {stats.activeSession}
          </span>
        )}
      </div>

      {/* Summary Cards — row 1: 4 cards */}
      <div className="grid mb-3">
        <div className="col-12 sm:col-6 lg:col-3">
          <StatCard
            title="Total Students"
            value={stats?.totalStudents || 0}
            icon="pi-users"
            color={colors.primary}
            subtitle="View students →"
            onClick={() => navigate('/index/student')}
          />
        </div>
        <div className="col-12 sm:col-6 lg:col-3">
          <StatCard
            title="Total Teachers"
            value={stats?.totalTeachers || 0}
            icon="pi-user"
            color={colors.success}
            subtitle="View teachers →"
            onClick={() => navigate('/index/teachers')}
          />
        </div>
        <div className="col-12 sm:col-6 lg:col-3">
          <StatCard
            title="Today's Attendance"
            value={`${stats?.todayAttendance?.rate || 0}%`}
            icon="pi-check-circle"
            color={colors.warning}
            subtitle={`${stats?.todayAttendance?.present || 0} present · View report →`}
            onClick={() => navigate('/index/attendance-report')}
          />
        </div>
        <div className="col-12 sm:col-6 lg:col-3">
          <StatCard
            title="Fee Collected"
            value={`${currency ? currency + " " : ""}${((stats?.feeCollection?.paid || 0) / 1000).toFixed(0)}k`}
            icon="pi-wallet"
            color={colors.purple}
            subtitle="View fee vouchers →"
            onClick={() => navigate('/index/fee-vouchers')}
          />
        </div>
      </div>

      {/* Summary Cards — row 2: 3 cards */}
      <div className="grid mb-4">
        <div className="col-12 sm:col-6 lg:col-4">
          <StatCard
            title="Pending Leaves"
            value={stats?.pendingLeaves || 0}
            icon="pi-clock"
            color={colors.danger}
            subtitle="View leaves →"
            onClick={() => navigate('/index/leaves')}
          />
        </div>
        <div className="col-12 sm:col-6 lg:col-4">
          <StatCard
            title="Active Enrollments"
            value={stats?.activeEnrollments || 0}
            icon="pi-user-plus"
            color={colors.teal}
            subtitle="View enrollments →"
            onClick={() => navigate('/index/enrollments')}
          />
        </div>
        <div className="col-12 sm:col-6 lg:col-4">
          <StatCard
            title="Waitlist"
            value={stats?.totalWaitlisted || 0}
            icon="pi-list"
            color={colors.indigo}
            subtitle="View waitlist →"
            onClick={() => navigate('/index/waitlist')}
          />
        </div>
      </div>

      {/* Charts Row 1 */}
      <div className="grid mb-4">
        <div className="col-12 lg:col-8">
          <Card className="chart-card h-full">
            <div className="flex justify-content-between align-items-center mb-3">
              <h3 className="text-xl font-semibold m-0">Attendance Trends</h3>
              <span className="text-500 text-sm">Last 14 days</span>
            </div>
            <div style={{ height: '300px' }}>
              <Line data={attendanceChartData} options={attendanceChartOptions} />
            </div>
          </Card>
        </div>
        <div className="col-12 lg:col-4">
          <Card className="chart-card h-full">
            <h3 className="text-xl font-semibold mb-3">Fee Status</h3>
            <div style={{ height: '300px' }}>
              <Doughnut data={feeStatusData} options={pieChartOptions} />
            </div>
          </Card>
        </div>
      </div>

      {/* Charts Row 2 */}
      <div className="grid mb-4">
        <div className="col-12 lg:col-6">
          <Card className="chart-card h-full">
            <h3 className="text-xl font-semibold mb-3">Students by Grade</h3>
            <div style={{ height: '400px' }}>
              <Bar data={gradeChartData} options={gradeChartOptions} />
            </div>
          </Card>
        </div>
        <div className="col-12 lg:col-6">
          <Card className="chart-card h-full">
            <h3 className="text-xl font-semibold mb-3">Fee Collection by Month</h3>
            <div style={{ height: '400px' }}>
              <Bar data={feeMonthlyData} options={barChartOptions} />
            </div>
          </Card>
        </div>
      </div>

      {/* Charts Row 3 */}
      <div className="grid">
        <div className="col-12 md:col-6 lg:col-4">
          <Card className="chart-card h-full">
            <h3 className="text-xl font-semibold mb-3">Gender Distribution</h3>
            <div style={{ height: '280px' }}>
              <Pie data={genderChartData} options={pieChartOptions} />
            </div>
          </Card>
        </div>
        <div className="col-12 md:col-6 lg:col-4">
          <Card className="chart-card h-full">
            <h3 className="text-xl font-semibold mb-3">Leave Status</h3>
            <div style={{ height: '280px' }}>
              <Pie data={leaveStatusData} options={pieChartOptions} />
            </div>
          </Card>
        </div>
        <div className="col-12 lg:col-4">
          <Card className="chart-card h-full">
            <h3 className="text-xl font-semibold mb-3">Quick Stats</h3>
            <div className="quick-stats">
              <div className="quick-stat-item">
                <span className="label">Total Parents</span>
                <span className="value">{stats?.totalParents || 0}</span>
              </div>
              <div className="quick-stat-item">
                <span className="label">Active Grades</span>
                <span className="value">{stats?.totalGrades || 0}</span>
              </div>
              <div className="quick-stat-item">
                <span className="label">Today Absent</span>
                <span className="value text-danger">{stats?.todayAttendance?.absent || 0}</span>
              </div>
              <div className="quick-stat-item">
                <span className="label">Today Late</span>
                <span className="value text-warning">{stats?.todayAttendance?.late || 0}</span>
              </div>
              <div className="quick-stat-item">
                <span className="label">Fee Pending</span>
                <span className="value text-warning">{currency ? currency + " " : ""}{((stats?.feeCollection?.unpaid || 0) / 1000).toFixed(0)}k</span>
              </div>
              <div className="quick-stat-item">
                <span className="label">Fee Overdue</span>
                <span className="value text-danger">{currency ? currency + " " : ""}{((stats?.feeCollection?.overdue || 0) / 1000).toFixed(0)}k</span>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
